import { Link as RouterLink } from 'react-router-dom';
import { s, type TypographyRole } from '@/theme/tokens';
import { Box, Link as MuiLink, Text } from '@/ui/primitives';
import { Chip } from '@/ui/primitives/Chip';
import { formatDate } from '../../../formatters/formatDate';
import { formatRelativeTime } from '../../../formatters/formatRelativeTime';
import { Inline } from '../../../layout/Inline';
import { Stack } from '../../../layout/Stack';
import { OverflowTooltip } from '../cells/OverflowTooltip';
import { buildUploadedAtMetaChip } from '../tableBuilders';
import { cellSpacingSx, linkTruncateSx, resolveIteratorLabel } from './shared';
import type { SxProps, Theme } from '@/ui/primitives';
import type { ReactElement } from 'react';

export type NameWithRunCellProps = {
  primary: LinkDetails;
  iterator?: IteratorDetails;
  secondary?: LinkDetails;
  iteratorRole?: TypographyRole;
  showLatest?: boolean;
  onLatestClick?: () => void;
  stackSx?: SxProps<Theme>;
};

export type ProjectCellProps = {
  label: string;
  href: string;
  onClick?: () => void;
  sx?: SxProps<Theme>;
  padding?: 'default' | 'none';
  role?: TypographyRole;
};

export type ResultsFileMetaCellProps = {
  fileName: string;
  fileHref: string;
  buildId?: string | null;
  buildHref?: string;
  uploaderLabel?: string | null;
  uploaderHref?: string;
  showBuild?: boolean;
};

export type UploadMetaCellProps = {
  uploadId: string;
  createdAt: Date | string | null;
  createdByName?: string | null;
  buildId?: string | null;
};

type LinkDetails = {
  label: string;
  href: string;
  onClick?: () => void;
  sx?: SxProps<Theme>;
  role?: TypographyRole;
};

type IteratorDetails = { value: string | number; href: string; onClick?: () => void };

type StackedChipMetaCellProps = {
  mainChip: ReactElement;
  metaChips: ReactElement[];
};

const metaCellSpacing = s('sm');

const StackedChipMetaCell = ({ mainChip, metaChips }: StackedChipMetaCellProps): ReactElement => (
  <Box sx={{ pt: metaCellSpacing, width: '100%', minWidth: 0 }}>
    <Stack gap="sm" align="flex-start">
      {mainChip}
      {metaChips.length > 0 ? (
        <Inline gap="xs" align="center" wrap="wrap">
          {metaChips}
        </Inline>
      ) : null}
    </Stack>
  </Box>
);

export const NameWithRunCell = ({
  primary,
  iterator,
  secondary,
  iteratorRole,
  showLatest = false,
  onLatestClick,
  stackSx,
}: NameWithRunCellProps): ReactElement => {
  const spacingSm = s('sm');
  const resolvedStackSx = stackSx
    ? Array.isArray(stackSx)
      ? [cellSpacingSx, ...stackSx]
      : [cellSpacingSx, stackSx]
    : cellSpacingSx;
  const resolvedPrimarySx = primary.sx
    ? Array.isArray(primary.sx)
      ? primary.sx
      : [primary.sx]
    : [];
  const resolvedSecondarySx = [
    linkTruncateSx,
    ...(secondary?.sx ? (Array.isArray(secondary.sx) ? secondary.sx : [secondary.sx]) : []),
  ];
  const primaryRole = primary.role ?? 'body';
  const resolvedIteratorRole = iteratorRole ?? 'body';
  const secondaryRole = secondary?.role ?? 'body';
  return (
    <Box sx={resolvedStackSx}>
      <Stack gap="sm">
        <Inline gap="sm" align="baseline" wrap="wrap">
          <Text role={primaryRole}>
            <OverflowTooltip title={primary.label} inline>
              <MuiLink
                component={RouterLink}
                to={primary.href}
                underline="hover"
                color="inherit"
                onClick={(event) => {
                  event.stopPropagation();
                  primary.onClick?.();
                }}
                sx={[linkTruncateSx, ...resolvedPrimarySx] as SxProps<Theme>}
              >
                {primary.label}
              </MuiLink>
            </OverflowTooltip>
          </Text>
          {iterator ? (
            <Text role={resolvedIteratorRole} color="textSecondary">
              <OverflowTooltip title={resolveIteratorLabel(iterator.value)} inline>
                <MuiLink
                  component={RouterLink}
                  to={iterator.href}
                  underline="hover"
                  color="inherit"
                  onClick={(event) => {
                    event.stopPropagation();
                    iterator.onClick?.();
                  }}
                  sx={linkTruncateSx}
                >
                  {resolveIteratorLabel(iterator.value)}
                </MuiLink>
              </OverflowTooltip>
            </Text>
          ) : null}
        </Inline>
        {secondary ? (
          <Text role={secondaryRole} color="textSecondary">
            <OverflowTooltip title={secondary.label} inline>
              <MuiLink
                component={RouterLink}
                to={secondary.href}
                underline="hover"
                color="inherit"
                onClick={(event) => {
                  event.stopPropagation();
                  secondary.onClick?.();
                }}
                sx={resolvedSecondarySx as SxProps<Theme>}
              >
                {secondary.label}
              </MuiLink>
            </OverflowTooltip>
          </Text>
        ) : null}
        {showLatest ? (
          <Box sx={{ width: 'fit-content', mt: spacingSm }}>
            <Chip
              size="xs"
              label="latest"
              tone="primary"
              variant="filled"
              clickable={Boolean(onLatestClick)}
              onClick={(event) => {
                event.stopPropagation();
                onLatestClick?.();
              }}
            />
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
};

export const ProjectCell = ({
  label,
  href,
  onClick,
  sx,
  padding = 'default',
  role = 'body',
}: ProjectCellProps): ReactElement => {
  const resolvedSx = [
    linkTruncateSx,
    ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
  ] as SxProps<Theme>;
  const link = (
    <OverflowTooltip title={label} inline>
      <MuiLink
        component={RouterLink}
        to={href}
        underline="hover"
        color="inherit"
        onClick={(event) => {
          event.stopPropagation();
          onClick?.();
        }}
        sx={resolvedSx}
      >
        {label}
      </MuiLink>
    </OverflowTooltip>
  );

  if (padding === 'none') {
    return (
      <Text role={role} component="span">
        {link}
      </Text>
    );
  }

  return (
    <Text role={role} component="span">
      {link}
    </Text>
  );
};

export const ResultsFileMetaCell = ({
  fileName,
  fileHref,
  buildId,
  buildHref,
  uploaderLabel,
  uploaderHref,
  showBuild = true,
}: ResultsFileMetaCellProps): ReactElement => {
  const hasBuild = Boolean(buildId);
  const hasUploader = Boolean(uploaderLabel);
  const hasUploaderLink = Boolean(uploaderHref);
  const resolvedBuildHref = buildHref ?? '/pipelines';
  return (
    <StackedChipMetaCell
      mainChip={
        <Chip
          size="xs"
          label={
            <MuiLink
              component={RouterLink}
              to={fileHref}
              underline="hover"
              color="inherit"
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              {fileName}
            </MuiLink>
          }
        />
      }
      metaChips={[
        ...(showBuild && hasBuild
          ? [
              <Chip
                key="build"
                size="xs"
                label={
                  <MuiLink
                    component={RouterLink}
                    to={resolvedBuildHref}
                    underline="hover"
                    color="inherit"
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                    sx={{
                      display: 'inline-flex',
                      minWidth: 0,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {buildId}
                  </MuiLink>
                }
              />,
            ]
          : []),
        ...(hasBuild || !hasUploader
          ? []
          : [
              <Chip
                key="uploader"
                size="xs"
                label={
                  hasUploaderLink ? (
                    <MuiLink
                      component={RouterLink}
                      to={uploaderHref ?? '#'}
                      underline="hover"
                      color="inherit"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      sx={{
                        display: 'inline-flex',
                        minWidth: 0,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {uploaderLabel}
                    </MuiLink>
                  ) : (
                    <Text role="body" component="span">
                      {uploaderLabel}
                    </Text>
                  )
                }
              />,
            ]),
      ]}
    />
  );
};

export const UploadMetaCell = ({
  uploadId,
  createdAt,
  createdByName,
  buildId,
}: UploadMetaCellProps): ReactElement => {
  const createdLabel = formatRelativeTime(createdAt);
  const createdTitle = formatDate(createdAt, { includeTime: true });
  const spacingSm = s('sm');
  return (
    <Box sx={{ mb: spacingSm }}>
      <StackedChipMetaCell
        mainChip={
          <Chip
            size="xs"
            label={
              <MuiLink
                component={RouterLink}
                to={`/uploads/${uploadId}`}
                underline="hover"
                color="inherit"
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                {uploadId}
              </MuiLink>
            }
          />
        }
        metaChips={[
          ...(buildId
            ? [<Chip key="build" size="xs" label={buildId} />]
            : createdByName
              ? [<Chip key="uploader" size="xs" label={createdByName} />]
              : []),
          buildUploadedAtMetaChip(createdLabel, createdTitle, 'uploaded-at'),
        ]}
      />
    </Box>
  );
};
